/**
 * Primitivas compartilhadas da aba Inteligência de Mercado (Fase 4).
 *
 * Briefing, Relatórios, Oportunidades e Calendário repetem os mesmos átomos
 * (link de MLB, chip de SKU, card de estatística, contagem regressiva de
 * dias) — eles moram AQUI para nenhum dos quatro reimplementar (CLAUDE.md §4).
 * Formatação numérica/data delega SEMPRE para `utils/` — nada de Intl inline.
 */
import { CaretRight } from 'shared/icons';
import { GetCampaignTypeLabel } from '../../constants/campaignTypes';
import { MLB_URLS } from '../../utils/ml';
import { Pill } from '../campaigns/campaignCardShared';

export const DASH = '—';

/**
 * Rótulos pt-BR dos tipos de campanha do ML. Espelham o map local de
 * `pages/Campaigns.jsx` (função não exportada de lá — importar página dentro
 * de componente inverteria a direção das dependências). Promover o map para
 * `constants/` é tarefa do dono do módulo quando unificar os consumidores.
 */
// F27: unificado em constants/campaignTypes — este arquivo só reexporta para
// os consumidores existentes não quebrarem.
export { CAMPAIGN_TYPE_LABELS } from '../../constants/campaignTypes';

/** Tipo → rótulo legível; desconhecido sai cru (melhor que esconder). */
export function CampaignTypeLabel(type) {
    return GetCampaignTypeLabel(type, DASH);
}

/**
 * Contagem de dias → texto curto pt-BR ("hoje", "amanhã", "em 5 dias").
 * `null`/negativo → '—': dia que já passou não é "em -2 dias".
 */
export function FormatDaysUntil(days) {
    const n = Number(days);
    if (!Number.isFinite(n) || n < 0) return DASH;
    if (n === 0) return 'hoje';
    if (n === 1) return 'amanhã';
    return `em ${Math.round(n)} dias`;
}

/** Link do anúncio no Seller Center — mesma URL builder do resto do app. */
export const MlbLink = ({ itemId, className = '' }) => {
    if (!itemId) {
        return <span className="text-mono-400">{DASH}</span>;
    }
    return (
        <a
            href={MLB_URLS.search(itemId)}
            target="_blank"
            rel="noreferrer"
            // stopPropagation: o link vive dentro de linhas/cards clicáveis —
            // abrir o anúncio não pode também disparar a navegação da linha.
            onClick={(event) => event.stopPropagation()}
            className={`font-mono text-xs text-blue-600 hover:underline dark:text-blue-400 ${className}`.trim()}
        >
            {itemId}
        </a>
    );
};

/** Chip monoespaçado de SKU (mesmo visual dos relatórios Legacy). */
export const SkuChip = ({ sku }) =>
    sku ? (
        <span className="rounded-md bg-mono-100 px-1.5 py-px font-mono text-[11px] text-mono-600 dark:bg-mono-900 dark:text-mono-300">
            {sku}
        </span>
    ) : (
        <span className="text-mono-400">{DASH}</span>
    );

/** Célula "título + SKU" padrão das tabelas de item da Inteligência. */
export const ItemCell = ({ title, sku }) => (
    <div className="min-w-0">
        <p className="truncate text-sm font-medium text-black dark:text-white" title={title || ''}>
            {title || DASH}
        </p>
        {sku ? (
            <p className="mt-0.5">
                <SkuChip sku={sku} />
            </p>
        ) : null}
    </div>
);

/**
 * Card de estatística da Inteligência. Promovido para `ui/StatCard` quando a
 * Visão geral de /campaigns precisou do mesmo card; o nome antigo fica aqui
 * como reexport para Briefing e Relatórios não mudarem.
 */
export { StatCard as IntelStatCard } from '../ui/StatCard';

/** Pílula de tipo de campanha (tom neutro — a cor fica para status/urgência). */
export const CampaignTypePill = ({ type }) =>
    type ? <Pill tone="mono">{CampaignTypeLabel(type)}</Pill> : <span className="text-mono-400">{DASH}</span>;

/** Estado de carregamento padrão dos painéis da Inteligência. */
export const IntelLoading = ({ text = 'Carregando…' }) => <p className="py-6 text-sm text-mono-500">{text}</p>;

/**
 * ISO "YYYY-MM-DD" → "DD/MM" (curto, eixo) e "DD/MM/YYYY" (tooltip).
 * String slicing puro — `new Date(iso)` + toLocale aplicaria o fuso do
 * browser e deslocaria a segunda-feira UTC do bucket em 1 dia (mesmo motivo
 * do DetailPanel/tabs/Sales.jsx). Usado pelos gráficos semanais de
 * Elasticidade e Demanda.
 */
export function FormatWeekLabels(iso) {
    const parts = String(iso ?? '')
        .slice(0, 10)
        .split('-');
    if (parts.length !== 3) {
        return { short: String(iso ?? ''), full: String(iso ?? '') };
    }
    return { short: `${parts[2]}/${parts[1]}`, full: `${parts[2]}/${parts[1]}/${parts[0]}` };
}

/** Modos de agregação do wire (`group_by`) das análises de vendas; 'item' é o default do servidor. */
export const GROUP_BY_OPTIONS = [
    { value: 'item', label: 'Anúncio (MLB)' },
    { value: 'sku', label: 'SKU' },
];

/**
 * Alternância da unidade de análise (Elasticidade e Demanda). Reusa o
 * desenho dos toggles de modo do app (WarehouseLocationsPage): trilho com
 * borda fina + pílula preta no ativo, `aria-pressed` em cada botão — um
 * <Select> de duas opções esconderia a segunda atrás de um clique, e essa
 * escolha muda a leitura da tabela inteira.
 */
export const GroupByToggle = ({ value, onChange }) => (
    <div>
        {/* Mesmas classes do <label> do ui/Input para alinhar com os campos
            vizinhos da barra de filtros (que é items-end). */}
        <span className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300">Analisar por</span>
        <div className="inline-flex shrink-0 rounded-lg border border-mono-300 p-0.5 dark:border-mono-700">
            {GROUP_BY_OPTIONS.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange(option.value)}
                    aria-pressed={value === option.value}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                        value === option.value
                            ? 'bg-black font-medium text-white dark:bg-white dark:text-black'
                            : 'text-mono-600 hover:bg-mono-100 dark:text-mono-400 dark:hover:bg-mono-900'
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>
    </div>
);

/**
 * Seta de estado do detalhe inline (fechada aponta para a direita, aberta
 * gira para baixo). Sem ela a linha expandida apareceria "do nada": o fundo
 * destacado diz QUE algo abriu, o caret diz QUAL linha abriu.
 */
export const ExpandCaret = ({ open }) => (
    <CaretRight
        size={14}
        aria-hidden="true"
        className={`shrink-0 text-mono-400 transition-transform ${open ? 'rotate-90' : ''}`}
    />
);

/** Par rótulo/valor compacto dos painéis de detalhe inline. */
export const DetailStat = ({ label, value, sub = null }) => (
    <div className="min-w-[120px]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-mono-400">{label}</p>
        <p className="mt-0.5 text-sm font-semibold tabular-nums text-black dark:text-white">{value}</p>
        {sub ? <p className="text-[11px] text-mono-500">{sub}</p> : null}
    </div>
);

/** Miniatura do anúncio/grupo (vazia quando o wire não trouxe thumbnail). */
export const RowThumb = ({ src }) => (
    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-900">
        {src ? <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" /> : null}
    </div>
);
